import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Table, Button, message, Popconfirm, Icon } from 'antd'
import axios from 'axios'
import './VersionHistoryModal.scss'
import { config } from '../../../../../constants'
import { getUserPermissions } from '../utils/getPermission'
import { useTranslation } from 'react-i18next'

const VersionHistoryModal = function({
  user,
  orgUserList,
  visible,
  onClose,
  file
}) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(false)

  const WOPI_HOST = config.questionAnswerAiUrl

  // Fetch versions when modal opens
  useEffect(
    function() {
      if (visible && file) {
        fetchVersions()
      }
    },
    [visible, file]
  )

  const fetchAccessToken = async () => {
    const shlToken = ''
    try {
      console.log('Fetching access token...')

      // Make API call to get token
      const response = await axios.post(
        `${WOPI_HOST}/api/documents/${file.path}/token`,
        {
          userId: String(user.info.id),
          userName: user.info.username || '',
          permissions: JSON.stringify(['edit', 'view'])
        },
        {
          headers: {
            Authorization: `Bearer ${shlToken}`,
            Accept: 'application/json'
          },
          timeout: 10000
        }
      )
      console.log('Token API Response:', response.data)
      // Extract token from response - adjust this based on your API response structure
      // You might need to modify this based on what your API returns
      const token = response.data.accessToken

      if (!token) {
        throw new Error('No token found in API response')
      }
      console.log('Fetched token:', token)
      return token
    } catch (error) {}
  }

  const fetchVersions = async () => {
    if (!file || !file.path) return

    setLoading(true)

    const token = await fetchAccessToken()

    fetch(
      `${WOPI_HOST}/api/documents/${file.path}/versions?access_token=${token}`
    )
      .then(function(response) {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error('Failed to fetch version history')
        }
      })
      .then(function(data) {
        console.log('data log', data.versions)
        setVersions(data.versions) // Updated to access data.versions
      })
      .catch(function(error) {
        console.error('Error fetching versions:', error)
        message.error('Error fetching version history')
      })
      .finally(function() {
        setLoading(false)
      })
  }

  const handleRestore = async (versionUuid, versionNumber) => {
    if (!file || !file.path) return

    const token = await fetchAccessToken()
    fetch(
      `${WOPI_HOST}/api/documents/${file.path}/versions/${versionUuid}/restore?access_token=${token}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
      .then(function(response) {
        if (response.ok) {
          message.success('Version ' + versionNumber + ' restored successfully')
          fetchVersions() // Refresh the list
          // Optionally close modal or refresh parent component
        } else {
          throw new Error('Failed to restore version')
        }
      })
      .catch(function(error) {
        console.error('Error restoring version:', error)
        message.error('Error restoring version')
      })
      .finally(function() {})
  }

  const handleDownload = async versionNumber => {
    if (!file || !file.path) return

    const token = await fetchAccessToken()
    fetch(
      `${WOPI_HOST}/api/documents/${file.path}/versions/${versionNumber}/download?access_token=${token}`
    )
      .then(function(response) {
        if (response.ok) {
          return response.blob()
        } else {
          throw new Error('Failed to download version')
        }
      })
      .then(function(blob) {
        var url = window.URL.createObjectURL(blob)
        var a = document.createElement('a')
        a.href = url
        a.download = (file.name || 'document') + '_v' + versionNumber
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        message.success('Download started')
      })
      .catch(function(error) {
        console.error('Error downloading version:', error)
        message.error('Error downloading version')
      })
      .finally(function() {})
  }

  const { isEdit, isView } = useMemo(() => {
    return file
      ? getUserPermissions(user, orgUserList, file, true)
      : { isEdit: false, isView: false }
  }, [file, user])

  const { t } = useTranslation()

  const columns = [
    {
      title: t('v5:version'),
      dataIndex: 'versionNumber', // Updated to match your data structure
      key: 'versionNumber',
      width: 100,
      render: function(text) {
        return <strong>{text}</strong>
      }
    },
    {
      title: t('v5:modified_by'),
      dataIndex: 'createdBy', // Updated to match your data structure
      key: 'createdBy',
      width: 150
    },
    {
      title: t('reports:time'),
      dataIndex: 'createdAt', // Updated to match your data structure
      key: 'createdAt',
      width: 180,
      render: function(text) {
        var date = new Date(text)
        return date.toLocaleString()
      }
    },
    // {
    //   title: 'Comment',
    //   dataIndex: 'comment', // Added comment column
    //   key: 'comment',
    //   width: 120,
    // },
    {
      title: t('v5:size'),
      dataIndex: 'size', // Added size column
      key: 'size',
      width: 100,
      render: function(text) {
        return (text / 1024).toFixed(2) + ' KB'
      }
    },
    {
      title: t('reports:download'),
      key: 'download',
      width: 100,
      render: function(_, record) {
        return (
          <Button
            type="link"
            icon="download"
            onClick={() => {
              if (isEdit) {
                handleDownload(record.versionPath)
              }
            }}
            title={isEdit ? 'Download this version' : 'Download not available'}
            style={{ padding: 0, height: 'auto' }}
          />
        )
      }
    },
    {
      title: t('v5:restore'),
      key: 'restore',
      width: 100,
      render: function(_, record) {
        return (
          <Popconfirm
            title={t('v5:are_you_sure_you_want_to_restore_this_version')}
            description={t(
              'v5:this_will_replce_the_current_version_with_the_selected_one'
            )}
            onConfirm={() => {
              handleRestore(record.versionUuid, record.versionNumber)
            }}
            okText="Yes"
            cancelText="No"
            disabled={!isEdit}
          >
            <Button
              type="link"
              icon="undo"
              disabled={!isEdit}
              title={
                isEdit
                  ? t('v5:restore_this_version')
                  : t('v5:restore_not_available')
              }
              style={{
                padding: 0,
                height: 'auto',
                color: isEdit ? '#ff4d4f' : undefined
              }}
            />
          </Popconfirm>
        )
      }
    }
  ]

  var fileTitle = ''
  if (file && file.title) {
    fileTitle = file.title
  } else if (file && file.name) {
    fileTitle = file.name
  }

  return (
    <Modal
      visible={visible}
      title={t('v5:version_history') + fileTitle}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('buttons:close')}
        </Button>
      ]}
      width={1000} // Increased width to accommodate new columns
      className="version-history-modal"
    >
      <Table
        columns={columns}
        dataSource={versions}
        loading={loading}
        rowKey="versionNumber" // Updated to match your data structure
        pagination={{
          pageSize: 5,
          showSizeChanger: false,
          showQuickJumper: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
        }}
        size="small"
        scroll={{ y: 400 }}
        locale={{
          emptyText: 'No version history available'
        }}
        rowClassName="version-history-row"
      />
    </Modal>
  )
}

export default VersionHistoryModal
